import { createContext, useContext, type ReactNode } from 'react';
import { DemoScaledPortalContext } from '~/design-system/components/InteractiveDemo/DemoScaledPortalContext';
import { useDemoScaleToFit } from '~/design-system/components/InteractiveDemo/useDemoScaleToFit';
import { cn } from '~/lib/utils';
import styles from './FeatureDemoScaleFit.module.css';

const FeatureDemoScaleContext = createContext(1);

export const useFeatureDemoScale = () => useContext(FeatureDemoScaleContext);

export type FeatureDemoScaleMode = 'zoom' | 'transform';

export interface FeatureDemoScaleFitProps {
  children: ReactNode;
  designWidth: number;
  designHeight: number;
  className?: string;
  /** Use `transform` for Tldraw demos — CSS `zoom` breaks viewport measurement. */
  scaleMode?: FeatureDemoScaleMode;
}

export const FeatureDemoScaleFit = ({
  children,
  designWidth,
  designHeight,
  className,
  scaleMode = 'zoom',
}: FeatureDemoScaleFitProps) => {
  const { hostRef, scale } = useDemoScaleToFit({ designWidth, designHeight });
  const stageStyle = {
    width: designWidth,
    height: designHeight,
    ['--demo-scale' as string]: scale,
  };

  return (
    <FeatureDemoScaleContext.Provider value={scale}>
      <div ref={hostRef} className={cn(styles.host, className)}>
        {scaleMode === 'transform' ? (
          <div
            className={styles.scaleSlot}
            style={{
              width: designWidth * scale,
              height: designHeight * scale,
            }}
          >
            <div
              className={styles.stageTransform}
              style={{
                ...stageStyle,
                transform: `translate(-50%, -50%) scale(${scale})`,
              }}
            >
              <DemoScaledPortalContext.Provider value={{ scale }}>
                {children}
              </DemoScaledPortalContext.Provider>
            </div>
          </div>
        ) : (
          <div
            className={styles.stage}
            style={{
              ...stageStyle,
              zoom: scale,
            }}
          >
            <DemoScaledPortalContext.Provider value={{ scale }}>
              {children}
            </DemoScaledPortalContext.Provider>
          </div>
        )}
      </div>
    </FeatureDemoScaleContext.Provider>
  );
};

FeatureDemoScaleFit.displayName = 'FeatureDemoScaleFit';
